import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import DashboardFilters from "@/components/DashboardFilters";
import { useTheme } from "@/contexts/ThemeContext";
import { useNavigate } from "react-router-dom";
import {
  IndianRupee, Users, FileText, RefreshCw,
  BarChart3, PieChart, Wallet, Receipt, Target,
  Search, ArrowUpDown, ChevronDown, ChevronUp, TrendingUp,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, Area, AreaChart, Legend,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#6366f1","#06b6d4","#10b981","#f59e0b","#ef4444","#ec4899","#8b5cf6","#14b8a6","#f97316","#84cc16"];

const chartGrid = (dark) => dark ? "#1e2231" : "#f1f5f9";
const chartAxis = (dark) => dark ? "#64748b" : "#94a3b8";
const chartTooltip = (dark) => ({
  borderRadius: "12px",
  border: dark ? "1px solid #1e2231" : "1px solid #e2e8f0",
  fontSize: 13,
  backgroundColor: dark ? "#12151e" : "#fff",
  color: dark ? "#e8ecf1" : "#0f172a",
});

const fmt = (v) => {
  if (v >= 100000) return `\u20B9${(v / 100000).toFixed(2)}L`;
  if (v >= 1000) return `\u20B9${(v / 1000).toFixed(1)}K`;
  return `\u20B9${Math.round(v).toLocaleString("en-IN")}`;
};

const fmtFull = (v) => `\u20B9${Math.round(v).toLocaleString("en-IN")}`;

const FinancialDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [resourceMappings, setResourceMappings] = useState([]);
  const [tableSearch, setTableSearch] = useState("");
  const [sortField, setSortField] = useState("total_amount");
  const [sortDir, setSortDir] = useState("desc");
  const [filtered, setFiltered] = useState(null);
  const [filterParams, setFilterParams] = useState({});
  const { dark } = useTheme();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, tasksRes, mappingsRes] = await Promise.all([
        axios.get(`${API}/dashboard/enhanced-stats`),
        axios.get(`${API}/adjustments?limit=10000`),
        axios.get(`${API}/resource-mappings`).catch(() => ({ data: { mappings: [] } })),
      ]);
      setStats(statsRes.data);
      setTasks(tasksRes.data.tasks || []);
      setResourceMappings(mappingsRes.data?.mappings || []);
    } catch (error) {
      toast.error("Failed to load expense data");
    } finally {
      setLoading(false);
    }
  };

  const fetchFiltered = useCallback(async (params) => {
    try {
      const qp = new URLSearchParams(params);
      const res = await axios.get(`${API}/dashboard/filtered?${qp}`);
      setFiltered(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { fetchFiltered(filterParams); }, [filterParams, fetchFiltered]);

  const handleFiltersChange = useCallback((params) => { setFilterParams(params); }, []);
  const navigate = useNavigate();
  const [trendGranularity, setTrendGranularity] = useState("monthly");

  // Lookup for resource display names
  const nameMap = useMemo(() => {
    const m = {};
    resourceMappings.forEach(r => {
      if (r.email && r.display_name) m[r.email] = r.display_name;
    });
    return m;
  }, [resourceMappings]);

  const displayName = (email) => nameMap[email] || email?.replace(/@graphikpro\.com$/, "") || "Unknown";

  /* ---- Computed Metrics ---- */
  const completedTasks = useMemo(() => tasks.filter(t => t.task_status === "Completed"), [tasks]);
  const totalExpense = useMemo(() => completedTasks.reduce((s, t) => s + (t.amount || 0), 0), [completedTasks]);
  const totalTasks = completedTasks.length;
  const totalDuration = useMemo(() => completedTasks.reduce((s, t) => s + (t.duration_words || 0), 0), [completedTasks]);
  const avgPerTask = totalTasks > 0 ? totalExpense / totalTasks : 0;

  /* ---- Resource Breakdown ---- */
  const resourceBreakdown = useMemo(() => {
    const map = {};
    completedTasks.forEach(t => {
      const r = t.resource || "Unknown";
      if (!map[r]) map[r] = { email: r, name: displayName(r), total_amount: 0, task_count: 0, total_duration: 0, tasks_by_type: {} };
      map[r].total_amount += t.amount || 0;
      map[r].task_count += 1;
      map[r].total_duration += t.duration_words || 0;
      const tt = t.task || "Other";
      map[r].tasks_by_type[tt] = (map[r].tasks_by_type[tt] || 0) + 1;
    });
    return Object.values(map);
  }, [completedTasks, nameMap]);

  const uniqueResources = resourceBreakdown.length;
  const avgPerResource = uniqueResources > 0 ? totalExpense / uniqueResources : 0;
  const highestResource = useMemo(() => {
    if (resourceBreakdown.length === 0) return null;
    return [...resourceBreakdown].sort((a, b) => b.total_amount - a.total_amount)[0];
  }, [resourceBreakdown]);

  /* ---- Task Type Expenses ---- */
  const taskTypeData = useMemo(() => {
    const map = {};
    completedTasks.forEach(t => {
      const tt = t.task || "Unknown";
      if (!map[tt]) map[tt] = { name: tt, amount: 0, count: 0, duration: 0 };
      map[tt].amount += t.amount || 0;
      map[tt].count += 1;
      map[tt].duration += t.duration_words || 0;
    });
    return Object.values(map).sort((a, b) => b.amount - a.amount);
  }, [completedTasks]);

  /* ---- Top 10 Resource Expenses (for bar chart) ---- */
  const topResources = useMemo(() =>
    [...resourceBreakdown].sort((a, b) => b.total_amount - a.total_amount).slice(0, 10).map(r => ({
      name: r.name,
      amount: r.total_amount,
      tasks: r.task_count,
    })),
  [resourceBreakdown]);

  /* ---- Monthly Expense Trend ---- */
  const monthlyData = useMemo(() => {
    const months = {};
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    completedTasks.forEach(t => {
      if (!t.created_at) return;
      const d = new Date(t.created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2,"0")}`;
      if (!months[key]) months[key] = { month: `${monthNames[d.getMonth()]} ${d.getFullYear()}`, expense: 0, count: 0 };
      months[key].expense += t.amount || 0;
      months[key].count++;
    });
    return Object.entries(months).sort(([a],[b]) => a.localeCompare(b)).map(([,v]) => v).slice(-12);
  }, [completedTasks]);

  /* ---- Expense Share Pie ---- */
  const expensePieData = useMemo(() =>
    taskTypeData.map((t, i) => ({ name: t.name, value: t.amount, color: COLORS[i % COLORS.length] })),
  [taskTypeData]);

  // Chart data — use filtered API data when filters active, else use local computation
  const chartTaskTypes = useMemo(() => {
    if (filtered?.has_filters) return (filtered.task_type_distribution || []).map(t => ({ name: t.name, amount: t.amount, count: t.count, duration: t.duration }));
    return taskTypeData;
  }, [filtered, taskTypeData]);

  const chartTopResources = useMemo(() => {
    if (filtered?.has_filters) return (filtered.top_resources || []).map(r => ({ name: r.name, amount: r.amount, tasks: r.count }));
    return topResources;
  }, [filtered, topResources]);

  const chartTrendData = useMemo(() => {
    if (trendGranularity === "daily" && filtered?.daily_volume?.length > 0) {
      return filtered.daily_volume.map(d => ({ month: d.date.slice(5), expense: 0, count: d.count }));
    }
    if (trendGranularity === "weekly" && filtered?.weekly_trends?.length > 0) {
      return filtered.weekly_trends.map(w => ({ month: w.week.slice(5), expense: w.amount, count: w.tasks }));
    }
    if (filtered?.has_filters) {
      const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      return (filtered.monthly_trends || []).map(m => {
        const [y, mo] = m.month.split("-");
        return { month: `${monthNames[parseInt(mo) - 1]} ${y}`, expense: m.amount, count: m.tasks };
      });
    }
    return monthlyData;
  }, [filtered, monthlyData, trendGranularity]);

  const chartPieData = useMemo(() =>
    chartTaskTypes.map((t, i) => ({ name: t.name, value: t.amount, color: COLORS[i % COLORS.length] })),
  [chartTaskTypes]);

  const chartResourceTable = useMemo(() => {
    if (filtered?.has_filters) {
      return (filtered.all_resources || filtered.top_resources || []).map(r => ({
        email: r.name, name: displayName(r.name), total_amount: r.amount, task_count: r.count, total_duration: r.duration, tasks_by_type: {}
      }));
    }
    return resourceBreakdown;
  }, [filtered, resourceBreakdown, displayName]);

  /* ---- Sorted/Filtered Resource Table ---- */
  const filteredResources = useMemo(() => {
    let data = [...chartResourceTable];
    if (tableSearch) {
      const q = tableSearch.toLowerCase();
      data = data.filter(r => r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q));
    }
    data.sort((a, b) => sortDir === "desc" ? b[sortField] - a[sortField] : a[sortField] - b[sortField]);
    return data;
  }, [chartResourceTable, tableSearch, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortField(field); setSortDir("desc"); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 text-slate-400" />;
    return sortDir === "desc" ? <ChevronDown className="w-3 h-3 text-indigo-500" /> : <ChevronUp className="w-3 h-3 text-indigo-500" />;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64" data-testid="financial-dashboard-loading">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">Loading expense data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="financial-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Expense Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Resource payments & cost breakdown (Completed tasks only)</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />Refresh
        </Button>
      </div>

      <DashboardFilters onFiltersChange={handleFiltersChange} onGranularityChange={setTrendGranularity} />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-indigo-500 to-violet-600 rounded-2xl p-5 text-white" data-testid="total-expense-card">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><Wallet className="w-5 h-5" /></div>
            <Badge className="bg-white/20 text-white text-[10px] hover:bg-white/20">Total</Badge>
          </div>
          <p className="text-2xl font-bold">{fmt(filtered?.has_filters ? filtered.total_amount : totalExpense)}</p>
          <p className="text-sm text-white/70 mt-1">Total Expenses</p>
        </div>

        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-5 text-white" data-testid="resources-card">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><Users className="w-5 h-5" /></div>
            <Badge className="bg-white/20 text-white text-[10px] hover:bg-white/20">{filtered?.has_filters ? filtered.unique_resources : uniqueResources} active</Badge>
          </div>
          <p className="text-2xl font-bold">{fmt(filtered?.has_filters ? filtered.avg_per_resource : avgPerResource)}</p>
          <p className="text-sm text-white/70 mt-1">Avg per Resource</p>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl p-5 text-white" data-testid="tasks-card">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><FileText className="w-5 h-5" /></div>
            <Badge className="bg-white/20 text-white text-[10px] hover:bg-white/20">{(filtered?.has_filters ? filtered.total_tasks : totalTasks).toLocaleString()} tasks</Badge>
          </div>
          <p className="text-2xl font-bold">{Math.round(filtered?.has_filters ? filtered.total_duration : totalDuration).toLocaleString("en-IN")}</p>
          <p className="text-sm text-white/70 mt-1">Total Duration / Words</p>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-5 text-white" data-testid="avg-card">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><Receipt className="w-5 h-5" /></div>
            {!filtered?.has_filters && highestResource && (
              <Badge className="bg-white/20 text-white text-[10px] hover:bg-white/20 truncate max-w-[100px]">{highestResource.name}</Badge>
            )}
          </div>
          <p className="text-2xl font-bold">{fmt(filtered?.has_filters && filtered.total_tasks > 0 ? filtered.total_amount / filtered.total_tasks : avgPerTask)}</p>
          <p className="text-sm text-white/70 mt-1">Avg per Task</p>
        </div>
      </div>

      {/* Charts Row 1: Trend + Expense Share Pie */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-indigo-600" />{trendGranularity === "daily" ? "Daily" : trendGranularity === "weekly" ? "Weekly" : "Monthly"} Expense Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            {monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={chartTrendData}>
                  <defs>
                    <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGrid(dark)} />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: chartAxis(dark) }} stroke={chartAxis(dark)} />
                  <YAxis tick={{ fontSize: 11, fill: chartAxis(dark) }} stroke={chartAxis(dark)} tickFormatter={v => fmt(v)} />
                  <Tooltip
                    formatter={(v, name) => [fmtFull(v), name === "expense" ? "Expense" : name]}
                    labelFormatter={l => l}
                    contentStyle={chartTooltip(dark)}
                  />
                  <Area type="monotone" dataKey="expense" stroke="#6366f1" strokeWidth={2} fillOpacity={1} fill="url(#expGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[260px] flex items-center justify-center text-slate-400 text-sm">No monthly data available</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <PieChart className="w-4 h-4 text-emerald-600" />Expense Share by Task Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            {expensePieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={180}>
                  <RechartsPie>
                    <Pie data={chartPieData} cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={4} dataKey="value">
                      {chartPieData.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip formatter={v => [fmtFull(v), "Expense"]} contentStyle={chartTooltip(dark)} />
                  </RechartsPie>
                </ResponsiveContainer>
                <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
                  {chartPieData.map((e, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: e.color }} />
                      <span className="text-[11px] text-slate-500">{e.name} ({fmt(e.value)})</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-slate-400 text-sm">No data</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: Top Resources + Task Type Expenses */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" />Top 10 Resource Expenses
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topResources.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartTopResources} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGrid(dark)} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: chartAxis(dark) }} stroke={chartAxis(dark)} tickFormatter={v => fmt(v)} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: chartAxis(dark) }} stroke={chartAxis(dark)} width={100} />
                  <Tooltip
                    formatter={(v, name) => [fmtFull(v), name === "amount" ? "Expense" : name]}
                    contentStyle={chartTooltip(dark)}
                  />
                  <Bar dataKey="amount" fill="#6366f1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400 text-sm">No data</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Target className="w-4 h-4 text-amber-600" />Expense by Task Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            {taskTypeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={taskTypeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGrid(dark)} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: chartAxis(dark) }} stroke={chartAxis(dark)} />
                  <YAxis tick={{ fontSize: 11, fill: chartAxis(dark) }} stroke={chartAxis(dark)} tickFormatter={v => fmt(v)} />
                  <Tooltip
                    formatter={(v, name) => {
                      if (name === "amount") return [fmtFull(v), "Expense"];
                      if (name === "count") return [v, "Tasks"];
                      return [v, name];
                    }}
                    contentStyle={chartTooltip(dark)}
                  />
                  <Bar dataKey="amount" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-slate-400 text-sm">No data</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Cost Per Unit + Language Breakdown + Daily Volume */}
      {filtered && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {/* Cost Per Unit */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Target className="w-4 h-4 text-teal-600" />Cost Per Unit</CardTitle></CardHeader>
            <CardContent>
              {filtered.cost_per_unit?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={filtered.cost_per_unit.slice(0, 8)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGrid(dark)} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: chartAxis(dark) }} stroke={chartAxis(dark)} tickFormatter={v => `₹${v}`} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: chartAxis(dark) }} stroke={chartAxis(dark)} width={70} />
                    <Tooltip formatter={(v) => [`₹${v.toLocaleString("en-IN")}`, "₹/Unit"]} contentStyle={chartTooltip(dark)} />
                    <Bar dataKey="cost_per_unit" fill="#14b8a6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="h-[220px] flex items-center justify-center text-sm text-slate-400">No data</div>}
            </CardContent>
          </Card>

          {/* Language Breakdown */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-violet-600" />Expense by Language</CardTitle></CardHeader>
            <CardContent>
              {filtered.language_breakdown?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={filtered.language_breakdown.slice(0, 8).map(l => ({ name: l.language, amount: l.amount }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGrid(dark)} />
                    <XAxis dataKey="name" tick={{ fontSize: 9, fill: chartAxis(dark) }} stroke={chartAxis(dark)} />
                    <YAxis tick={{ fontSize: 10, fill: chartAxis(dark) }} stroke={chartAxis(dark)} tickFormatter={v => fmt(v)} />
                    <Tooltip formatter={(v) => [fmtFull(v), "Expense"]} contentStyle={chartTooltip(dark)} />
                    <Bar dataKey="amount" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="h-[220px] flex items-center justify-center text-sm text-slate-400">No data</div>}
            </CardContent>
          </Card>

          {/* Daily Volume */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><TrendingUp className="w-4 h-4 text-blue-600" />Daily Task Volume</CardTitle></CardHeader>
            <CardContent>
              {filtered.daily_volume?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={filtered.daily_volume.map(d => ({ date: d.date.slice(5), count: d.count }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartGrid(dark)} />
                    <XAxis dataKey="date" tick={{ fontSize: 9, fill: chartAxis(dark) }} stroke={chartAxis(dark)} />
                    <YAxis tick={{ fontSize: 10, fill: chartAxis(dark) }} stroke={chartAxis(dark)} />
                    <Tooltip contentStyle={chartTooltip(dark)} />
                    <Area type="monotone" dataKey="count" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} strokeWidth={2} name="Tasks" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <div className="h-[220px] flex items-center justify-center text-sm text-slate-400">No data</div>}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Resource Expense Breakdown Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <IndianRupee className="w-4 h-4 text-indigo-600" />Resource Expense Breakdown
              <Badge variant="secondary" className="text-[10px]">{filteredResources.length} resources</Badge>
            </CardTitle>
            <div className="relative w-56">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <Input
                placeholder="Search resource..."
                value={tableSearch}
                onChange={e => setTableSearch(e.target.value)}
                className="pl-8 h-8 text-xs"
                data-testid="resource-table-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="resource-expense-table">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">#</th>
                  <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Resource</th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("task_count")}>
                    <span className="inline-flex items-center gap-1">Tasks <SortIcon field="task_count" /></span>
                  </th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("total_duration")}>
                    <span className="inline-flex items-center gap-1">Duration <SortIcon field="total_duration" /></span>
                  </th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:text-slate-700" onClick={() => toggleSort("total_amount")}>
                    <span className="inline-flex items-center gap-1">Expense <SortIcon field="total_amount" /></span>
                  </th>
                  <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">% Share</th>
                </tr>
              </thead>
              <tbody>
                {filteredResources.map((r, idx) => (
                  <tr key={r.email} className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => navigate(`/resource-center?search=${encodeURIComponent(r.email)}`)}>
                    <td className="py-2.5 px-3 text-xs text-slate-400">{idx + 1}</td>
                    <td className="py-2.5 px-3">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{r.name}</p>
                        <p className="text-[11px] text-slate-400">{r.email}</p>
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-right text-sm text-slate-700">{r.task_count}</td>
                    <td className="py-2.5 px-3 text-right text-sm text-slate-700">{Math.round(r.total_duration).toLocaleString("en-IN")}</td>
                    <td className="py-2.5 px-3 text-right text-sm font-semibold text-slate-900">{fmtFull(r.total_amount)}</td>
                    <td className="py-2.5 px-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-indigo-500 rounded-full"
                            style={{ width: `${totalExpense > 0 ? (r.total_amount / totalExpense) * 100 : 0}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500 w-10 text-right">
                          {totalExpense > 0 ? ((r.total_amount / totalExpense) * 100).toFixed(1) : "0"}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredResources.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-sm text-slate-400">No resources found</td>
                  </tr>
                )}
              </tbody>
              {filteredResources.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-50">
                    <td className="py-3 px-3" />
                    <td className="py-3 px-3 text-sm font-bold text-slate-800">Total ({filteredResources.length} resources)</td>
                    <td className="py-3 px-3 text-right text-sm font-bold text-slate-800">{filteredResources.reduce((s, r) => s + r.task_count, 0)}</td>
                    <td className="py-3 px-3 text-right text-sm font-bold text-slate-800">{Math.round(filteredResources.reduce((s, r) => s + r.total_duration, 0)).toLocaleString("en-IN")}</td>
                    <td className="py-3 px-3 text-right text-sm font-bold text-slate-900">{fmtFull(filteredResources.reduce((s, r) => s + r.total_amount, 0))}</td>
                    <td className="py-3 px-3 text-right text-xs text-slate-500">100%</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Summary Footer Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-testid="stat-total-tasks">
          <div className="flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-slate-400" /><span className="text-[11px] text-slate-500">Completed Tasks</span></div>
          <p className="text-lg font-bold text-slate-800">{totalTasks.toLocaleString()}</p>
        </div>
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-testid="stat-resources">
          <div className="flex items-center gap-2 mb-2"><Users className="w-4 h-4 text-slate-400" /><span className="text-[11px] text-slate-500">Active Resources</span></div>
          <p className="text-lg font-bold text-slate-800">{uniqueResources}</p>
        </div>
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-testid="stat-avg-resource">
          <div className="flex items-center gap-2 mb-2"><IndianRupee className="w-4 h-4 text-slate-400" /><span className="text-[11px] text-slate-500">Avg / Resource</span></div>
          <p className="text-lg font-bold text-slate-800">{fmt(avgPerResource)}</p>
        </div>
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-testid="stat-avg-task">
          <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-slate-400" /><span className="text-[11px] text-slate-500">Avg / Task</span></div>
          <p className="text-lg font-bold text-slate-800">{fmt(avgPerTask)}</p>
        </div>
      </div>
    </div>
  );
};

export default FinancialDashboard;
